import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Sparkles, Star, Zap, Mail, Phone, Building2, Briefcase, MapPin, Calendar, Filter, Search, Plus } from 'lucide-react';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';

const EmployeeLists = ({ onNavigate }) => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  // Status color mapping
  const statusColors = {
    'Active': 'bg-green-100 text-green-800 border-green-200',
    'Inactive': 'bg-gray-100 text-gray-800 border-gray-200',
    'On Leave': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Remote': 'bg-blue-100 text-blue-800 border-blue-200'
  };

  // Role color mapping
  const roleColors = {
    'Manager': 'bg-purple-100 text-purple-800 border-purple-200',
    'Developer': 'bg-blue-100 text-blue-800 border-blue-200',
    'Designer': 'bg-pink-100 text-pink-800 border-pink-200',
    'Analyst': 'bg-orange-100 text-orange-800 border-orange-200',
    'Tester': 'bg-red-100 text-red-800 border-red-200',
    'Admin': 'bg-gray-100 text-gray-800 border-gray-200'
  };

  // Fetch employees from API
  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No access token found');
        return;
      }

      let companyId;
      try {
        const decodedToken = jwtDecode(token);
        companyId = decodedToken.role === 'company'
          ? (decodedToken.companyId || decodedToken.id)
          : decodedToken.companyId;
      } catch (err) {
        console.error('Error decoding token:', err);
        return;
      }

      if (!companyId) {
        console.error('No company ID found in token');
        return;
      }

      const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/employees/company/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const employeeList = Array.isArray(data) ? data : (data.data || []);
        // Don't filter by isActive - show all employees
        setEmployees(employeeList);
        console.log('✅ Employees fetched:', employeeList.length, 'employees');
      } else {
        console.error('Failed to fetch employees:', response.status);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Fetch departments from API
  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('❌ No access token found');
        return;
      }

      let companyId;
      try {
        const decodedToken = jwtDecode(token);
        companyId = decodedToken.role === 'company'
          ? (decodedToken.companyId || decodedToken.id)
          : decodedToken.companyId;
      } catch (err) {
        console.error('Error decoding token:', err);
        return;
      }

      if (!companyId) {
        console.error('No company ID found in token');
        return;
      }

      console.log('Fetching departments...');

      const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📊 Departments response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Departments data received:', data);
        setDepartments(Array.isArray(data) ? data : (data.data || []));
      } else {
        console.error('❌ Failed to fetch departments:', response.status, response.statusText);
        // Try to read the error message
        try {
          const errorText = await response.text();
          console.error('❌ Error response:', errorText);
        } catch (e) {
          console.error('❌ Could not read error response');
        }
      }
    } catch (error) {
      console.error('❌ Error fetching departments:', error);
    }
  };

  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No access token found');
        return;
      }

      const decodedToken = jwtDecode(token);
      const userRole = decodedToken.role;
      const userId = decodedToken.id || decodedToken._id;
      console.log('EmployeeLists - User role:', userRole, 'User ID:', userId);

      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/projectsmgnt', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        let data = await response.json();
        let projectList = Array.isArray(data) ? data : [];
        console.log('EmployeeLists - Fetched projects (before filter):', projectList.length);

        // Filter projects based on user role
        if (userRole === 'employee' || userRole === 'team_member') {
          projectList = projectList.filter(project => {
            const isTeamMember = project.teamMembers && Array.isArray(project.teamMembers) &&
              project.teamMembers.some(member => {
                const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                return String(memberId) === String(userId);
              });

            console.log(`EmployeeLists - Project "${project.title || project.name}" - isTeamMember:`, isTeamMember);
            return isTeamMember;
          });

          console.log('EmployeeLists - Filtered projects for team member:', projectList.length);
        }

        setProjects(projectList);
        console.log('EmployeeLists - Projects set:', projectList.length);
      } else {
        console.error('Failed to fetch projects:', response.status);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Load all data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchEmployees(),
        fetchDepartments(),
        fetchProjects()
      ]);
      setLoading(false);
    };

    loadData();
  }, []);

  const getDepartmentName = (employee) => {
    console.log('Getting department for employee:', employee.teamMemberName || employee.name);
    console.log('Employee department data:', employee.department);
    console.log('Available departments count:', departments.length);

    if (!employee.department) return 'N/A';

    // Handle array of departments (most common case based on your data)
    if (Array.isArray(employee.department)) {
      console.log('Department is array with length:', employee.department.length);

      const departmentNames = [];

      employee.department.forEach((dept, index) => {
        console.log(`Processing department ${index}:`, dept);

        if (typeof dept === 'object' && dept !== null) {
          // Department is an object with name
          if (dept.name || dept.departmentName) {
            departmentNames.push(dept.name || dept.departmentName);
            console.log('✅ Found dept name from object:', dept.name || dept.departmentName);
          } else if (dept._id || dept.id) {
            // Department object has ID, look up name
            const deptId = dept._id || dept.id;
            const foundDept = departments.find(d =>
              String(d._id) === String(deptId) ||
              String(d.id) === String(deptId) ||
              String(d.departmentId) === String(deptId)
            );
            if (foundDept) {
              departmentNames.push(foundDept.name || foundDept.departmentName || `Department ${deptId}`);
              console.log('✅ Found dept name from lookup:', foundDept.name || foundDept.departmentName);
            } else {
              departmentNames.push(`Dept ID: ${deptId}`);
              console.log('❌ Dept not found for ID:', deptId);
            }
          }
        } else if (typeof dept === 'string') {
          // Department is a string ID
          const foundDept = departments.find(d =>
            String(d._id) === String(dept) ||
            String(d.id) === String(dept) ||
            String(d.departmentId) === String(dept)
          );
          if (foundDept) {
            departmentNames.push(foundDept.name || foundDept.departmentName || `Department ${dept}`);
            console.log('✅ Found dept name from string lookup:', foundDept.name || foundDept.departmentName);
          } else {
            departmentNames.push(`Dept ID: ${dept}`);
            console.log('❌ Dept not found for string ID:', dept);
          }
        }
      });

      const result = departmentNames.length > 0 ? departmentNames.join(', ') : 'Unknown Department';
      console.log('Final department names:', result);
      return result;
    }

    // Handle single department object
    if (typeof employee.department === 'object' && employee.department !== null) {
      if (employee.department.name || employee.department.departmentName) {
        return employee.department.name || employee.department.departmentName;
      }
      const deptId = employee.department._id || employee.department.id;
      if (deptId) {
        const foundDept = departments.find(d =>
          String(d._id) === String(deptId) ||
          String(d.id) === String(deptId) ||
          String(d.departmentId) === String(deptId)
        );
        return foundDept ? (foundDept.name || foundDept.departmentName || `Department ${deptId}`) : `Dept ID: ${deptId}`;
      }
    }

    // Handle single department string
    if (typeof employee.department === 'string' && employee.department.trim() !== '') {
      const deptId = employee.department.trim();
      const foundDept = departments.find(d =>
        String(d._id) === String(deptId) ||
        String(d.id) === String(deptId) ||
        String(d.departmentId) === String(deptId)
      );
      return foundDept ? (foundDept.name || foundDept.departmentName || `Department ${deptId}`) : `Dept ID: ${deptId}`;
    }

    return 'N/A';
  };

  // Get employee projects
  const getEmployeeProjects = (employeeId) => {
    return projects.filter((project) => {
      if (!Array.isArray(project.teamMembers)) return false;
      return project.teamMembers.some((member) => {
        const memberId = typeof member === 'object' ? member._id : member;
        return String(memberId) === String(employeeId);
      });
    });
  };

  // Handle project click
  const handleProjectClick = (projectId) => {
    if (onNavigate) {
      onNavigate(`/projects/${projectId}`, 'Project Details');
    }
  };

  // Handle employee card click
  const handleEmployeeCardClick = (employee) => {
    const employeeId = employee._id;
    const employeeName = employee.teamMemberName || employee.name || 'Unknown Employee';

    if (onNavigate) {
      onNavigate(`/projects?employeeId=${employeeId}&employeeName=${encodeURIComponent(employeeName)}`, 'Projects');
    } else {
      const assignedProjects = getEmployeeProjects(employee._id);
      navigate('/projects', {
        state: {
          employeeFilter: {
            id: employee._id,
            name: employeeName,
            projects: assignedProjects
          }
        }
      });
    }
  };

  // Filter employees based on search and filters
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = !searchTerm ||
      (employee.teamMemberName || employee.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.email || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDepartment = departmentFilter === 'all' ||
      getDepartmentName(employee).toLowerCase().includes(departmentFilter.toLowerCase());

    const matchesRole = roleFilter === 'all' ||
      (employee.role || '').toLowerCase().includes(roleFilter.toLowerCase());

    return matchesSearch && matchesDepartment && matchesRole;
  });

  // Get unique roles for filter
  const uniqueRoles = [...new Set(employees.map(emp => emp.role).filter(Boolean))];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading team members...</p>
        </div>
      </div>
    );
  }

  const tableContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '1rem',
    overflowX: 'auto',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
    marginBottom: '2rem'
  };

  const thStyle = {
    padding: '1.25rem 1rem',
    backgroundColor: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
    textAlign: 'left',
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: '1.25rem 1rem',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '0.9rem',
    color: '#4b5563',
    verticalAlign: 'middle'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Main Content */}
      <div className="max-w-[98%] mx-auto px-2 sm:px-4 lg:px-4 py-6">
        {/* Search and Filters */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search team members by name, role, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 backdrop-blur-sm z-10"
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept.name || dept.departmentName}>
                    {dept.name || dept.departmentName}
                  </option>
                ))}
              </select>

              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 backdrop-blur-sm"
              >
                <option value="all">All Roles</option>
                {uniqueRoles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-xl shadow-md">
              <div className="text-xl font-bold">{employees.length}</div>
              <div className="text-blue-100 text-xs">Total Members</div>
            </div>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-xl shadow-md">
              <div className="text-xl font-bold">
                {employees.filter(emp => emp.isActive !== false && emp.status !== 'Inactive').length}
              </div>
              <div className="text-blue-100 text-xs">Active</div>
            </div>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-xl shadow-md">
              <div className="text-xl font-bold">
                {departments.length}
              </div>
              <div className="text-blue-100 text-xs">Departments</div>
            </div>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-xl shadow-md">
              <div className="text-xl font-bold">
                {projects.length}
              </div>
              <div className="text-blue-100 text-xs">Total Projects</div>
            </div>
          </div>
        </div>

        {/* Employees Table (Desktop) */}
        <div style={tableContainerStyle} className="hidden md:block">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1100px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Team Member</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Department</th>
                <th style={thStyle}>Contact</th>
                <th style={thStyle}>Projects</th>
                <th style={thStyle}>Joined</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                    <div className="flex flex-col items-center">
                      <div className="text-6xl mb-4">👥</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {employees.length === 0 ? "No team members yet" : "No members match your search"}
                      </h3>
                      <p className="text-gray-600">
                        {employees.length === 0 ? "Add your first team member to get started." : "Try adjusting your search criteria."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((employee, index) => {
                  const assignedProjects = getEmployeeProjects(employee._id);
                  const employeeStatus = employee.status || 'Active';
                  const employeeRole = employee.role || 'Team Member';
                  const deptName = getDepartmentName(employee);

                  return (
                    <tr
                      key={employee._id}
                      style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      onClick={() => handleEmployeeCardClick(employee)}
                    >
                      <td style={tdStyle}>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white shadow-sm">
                            <Users className="w-5 h-5" />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: '#111827' }}>
                              {employee.teamMemberName || employee.name}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {employeeRole}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusColors[employeeStatus] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                          {employeeStatus}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-4 h-4 text-purple-500" />
                          <span style={{ fontSize: '0.85rem' }}>{deptName}</span>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2 text-gray-600">
                            <Mail className="w-3.5 h-3.5 text-blue-500" />
                            <span className="text-xs truncate max-w-[150px]">{employee.email}</span>
                          </div>
                          {employee.phone && (
                            <div className="flex items-center space-x-2 text-gray-600">
                              <Phone className="w-3.5 h-3.5 text-green-500" />
                              <span className="text-xs">{employee.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div className="flex items-center space-x-2">
                          <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-bold">
                            {assignedProjects.length}
                          </div>
                          <div className="flex -space-x-1 overflow-hidden">
                            {assignedProjects.slice(0, 2).map((p, idx) => (
                              <div
                                key={idx}
                                className="inline-block h-6 w-6 rounded-full ring-2 ring-white bg-gray-100 flex items-center justify-center"
                                title={p.title || p.name}
                              >
                                <Sparkles className="w-3 h-3 text-blue-500" />
                              </div>
                            ))}
                            {assignedProjects.length > 2 && (
                              <div className="inline-block h-6 w-6 rounded-full ring-2 ring-white bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600">
                                +{assignedProjects.length - 2}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div className="text-xs text-gray-500">
                          <div>J: {employee.createdAt ? new Date(employee.createdAt).toLocaleDateString() : 'N/A'}</div>
                          <div>U: {employee.updatedAt ? new Date(employee.updatedAt).toLocaleDateString() : 'N/A'}</div>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEmployeeCardClick(employee);
                          }}
                          className="p-2 bg-gray-50 hover:bg-blue-50 text-blue-600 rounded-lg border border-gray-200 transition-all duration-200"
                          title="View Details"
                        >
                          <Search className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Employees Cards (Mobile) */}
        <div className="md:hidden flex flex-col gap-4 mb-8">
          {filteredEmployees.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-200">
              <div className="text-4xl mb-3">👥</div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                {employees.length === 0 ? "No team members yet" : "No members match your search"}
              </h3>
              <p className="text-sm text-gray-600">
                {employees.length === 0 ? "Add your first team member to get started." : "Try adjusting your search criteria."}
              </p>
            </div>
          ) : (
            filteredEmployees.map((employee, index) => {
              const assignedProjects = getEmployeeProjects(employee._id);
              const employeeStatus = employee.status || 'Active';
              const employeeRole = employee.role || 'Team Member';
              const deptName = getDepartmentName(employee);

              return (
                <div
                  key={employee._id}
                  onClick={() => handleEmployeeCardClick(employee)}
                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex flex-col gap-3 active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white shadow-sm">
                        <Users className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-base leading-tight">
                          {employee.teamMemberName || employee.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {employeeRole}
                        </div>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold border whitespace-nowrap ${statusColors[employeeStatus] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                      {employeeStatus}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-1.5 pt-2 border-t border-gray-100">
                    <div className="flex items-center space-x-2 text-gray-700 text-sm">
                      <Building2 className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                      <span className="truncate">{deptName}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-700 text-sm">
                      <Mail className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      <span className="truncate">{employee.email}</span>
                    </div>
                    {employee.phone && (
                      <div className="flex items-center space-x-2 text-gray-700 text-sm">
                        <Phone className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        <span>{employee.phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 font-medium">Projects:</span>
                      <div className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-bold">
                        {assignedProjects.length}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEmployeeCardClick(employee);
                      }}
                      className="text-blue-600 bg-blue-50 p-2 rounded-lg"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Load More Button */}
        {filteredEmployees.length > 0 && (
          <div className="text-center mt-4">
            <button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
              Load More Team Members
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeLists;
